import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../config';
import { Folder, Plus, Edit2, Trash2, Loader2, AlertCircle } from 'lucide-react';

const SuperAdminCourseCategories = ({ authToken }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ name: '', slug: '', description: '', display_order: 0, is_active: true });
  const [saving, setSaving] = useState(false);

  const fetchCategories = () => {
    setLoading(true);
    setError('');
    fetch(`${API_BASE_URL}/admin/super/course-categories`, { headers: { Authorization: `Bearer ${authToken}` } })
      .then((res) => {
        if (res.status === 404) return Promise.reject(new Error('Backend route not found. Run Flask locally (python app.py) or deploy super admin routes.'));
        return res.ok ? res.json() : res.json().then((e) => Promise.reject(e));
      })
      .then((data) => setCategories(data.categories || []))
      .catch((err) => setError(err.message || err.error || 'Failed to load categories'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchCategories(); }, [authToken]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSaving(true);
    const url = modal?.id ? `${API_BASE_URL}/admin/super/course-categories/${modal.id}` : `${API_BASE_URL}/admin/super/course-categories`;
    const method = modal?.id ? 'PUT' : 'POST';
    fetch(url, {
      method,
      headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(form),
    })
      .then((res) => (res.ok ? res.json() : res.json().then((e) => Promise.reject(e))))
      .then(() => { setModal(null); setForm({ name: '', slug: '', description: '', display_order: 0, is_active: true }); fetchCategories(); })
      .catch((err) => setError(err.message || err.error || 'Save failed'))
      .finally(() => setSaving(false));
  };

  const openEdit = (cat) => {
    setModal(cat);
    setForm({
      name: cat.name || '',
      slug: cat.slug || '',
      description: cat.description || '',
      display_order: cat.display_order ?? 0,
      is_active: cat.is_active !== 0 && cat.is_active !== false
    });
  };

  const deleteCat = (id) => {
    if (!window.confirm('Delete this category?')) return;
    fetch(`${API_BASE_URL}/admin/super/course-categories/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${authToken}` } })
      .then((res) => (res.ok ? res.json() : res.json().then((e) => Promise.reject(e))))
      .then(() => { setModal(null); fetchCategories(); })
      .catch((err) => setError(err.message || err.error || 'Delete failed'));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-xl font-bold text-gray-900 mb-4">Course Categories (Platform)</h2>
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-center text-red-700">
          <AlertCircle className="h-4 w-4 mr-2" />
          {error}
        </div>
      )}
      <div className="mb-4 flex justify-end">
        <button
          type="button"
          onClick={() => { setModal({}); setForm({ name: '', slug: '', description: '', display_order: 0, is_active: true }); }}
          className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
        >
          <Plus className="h-4 w-4 mr-2" /> Add Category
        </button>
      </div>
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Active</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {categories.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-gray-500">No categories yet.</td>
              </tr>
            ) : (
              categories.map((cat) => (
                <tr key={cat.id}>
                  <td className="px-4 py-3 text-sm text-gray-900">{cat.name}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{cat.display_order}</td>
                  <td className="px-4 py-3 text-sm">{cat.is_active ? 'Yes' : 'No'}</td>
                  <td className="px-4 py-3 text-right">
                    <button type="button" onClick={() => openEdit(cat)} className="text-indigo-600 hover:underline mr-2">Edit</button>
                    <button type="button" onClick={() => deleteCat(cat.id)} className="text-red-600 hover:underline">Delete</button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {modal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-semibold mb-4">{modal.id ? 'Edit Category' : 'Add Category'}</h3>
              <form onSubmit={handleSubmit}>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <input type="text" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border rounded px-3 py-2" required />
                    <input type="text" placeholder="Slug (optional)" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className="w-full border rounded px-3 py-2" />
                  </div>
                  <textarea placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full border rounded px-3 py-2" rows={2} />
                  <input type="number" placeholder="Display order" value={form.display_order} onChange={(e) => setForm({ ...form, display_order: parseInt(e.target.value, 10) || 0 })} className="w-full border rounded px-3 py-2" />
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
                    Active
                  </label>
                </div>
                <div className="mt-4 flex justify-end gap-2">
                  <button type="button" onClick={() => setModal(null)} className="px-4 py-2 border rounded">Cancel</button>
                  <button type="submit" disabled={saving} className="px-4 py-2 bg-indigo-600 text-white rounded disabled:opacity-50">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin inline" /> : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminCourseCategories;
